import { AgentLauncher } from '../aiAgent/AgentLauncher'
import { useAuth } from '../../context/AuthContext'
import React, { useEffect, useState } from 'react'
import { Navigation } from './Navigation'
import { WorkspaceSwitcher } from '../workspaces/WorkspaceSwitcher'
import { AdminWorkspace } from '../workspaces/AdminWorkspace'
import { ShiftImport } from '../workspaces/ShiftImport'
import { SuperAdmin } from '../workspaces/SuperAdmin'
import { Supervisor } from '../workspaces/Supervisor'
import { Mapper } from '../workspaces/Mapper'
import { RBAC } from '../workspaces/RBAC'
import { Billing } from '../workspaces/Billing'
import { SalesAdministrationWorkspace } from '../workspaces/sales/SalesAdministrationWorkspace'
import { SalesCrmWorkspace } from '../workspaces/sales/SalesCrmWorkspace'
import { WorkerWorkspace } from '../workspaces/worker/WorkerWorkspace'
import { WorkerMentorshipPanel } from '../workspaces/worker/WorkerMentorshipPanel'
import { MentorAdminWorkspace } from '../workspaces/MentorAdminWorkspace'
import { LearningWorkspace } from '../workspaces/LearningWorkspace'
import { PlatformConsole } from '../workspaces/platform/PlatformConsole'
import { PersonalJobBankWorkspace } from '../workspaces/PersonalJobBankWorkspace'
import { JobBankWorkspace } from '../workspaces/JobBankWorkspace'
import { AgencyMarketOpportunitiesWorkspace } from '../workspaces/AgencyMarketOpportunitiesWorkspace'
import { BillingFeatureGate } from '../billing/BillingFeatureGate'
import { useAccessContext } from '../../context/AccessContext'
import type { LocationJobDraft } from '../workspaces/admin/adminNavigation'
import { SetupWizard } from '../setup/SetupWizard'
import { ErrorBoundary } from '../ui'
import { useI18n } from '../../i18n/I18nContext'
import { navigateTo } from '../../services/navigation'
import {
  guardedWorkspace,
  resolveDefaultWorkspace,
  isPlatformUser,
  workspaceFromLocation,
  workspaceUrl,
  type NavigationAccessInput,
  type WorkspaceView,
} from '../workspaces/admin/navigationRegistry'
import { AdminExperienceSwitcher } from '../workspaces/admin/AdminExperienceControls'
import { useViewportMode } from '../../hooks/useViewport'
import { WorkspaceCommands } from './WorkspaceCommands'

export type { WorkspaceView } from '../workspaces/admin/navigationRegistry'

const readLocationKey = (): string =>
  typeof window === 'undefined' ? '/admin' : `${window.location.pathname}${window.location.search}`

export const AdminShell: React.FC = () => {
  const { accessContext, loading, error, authenticationRequired, reauthenticationTenantId, refresh, selectedTenantId, currentOrganization, currentBranding, hasAnyPermission } = useAccessContext()
  const { logout } = useAuth()
  const { t } = useI18n()
  const viewport = useViewportMode()
  // `locationKey` tracks pathname + search so workspace changes carried in the
  // query string (e.g. `/admin?workspace=jobs`) trigger a re-resolve too.
  const [locationKey, setLocationKey] = useState(readLocationKey)
  const path = locationKey.split('?')[0]
  const [activeWorkspace, setActiveWorkspace] = useState<WorkspaceView>(() => workspaceFromLocation())
  const [initialJobDraft, setInitialJobDraft] = useState<LocationJobDraft | null>(null)
  const canManageJobs = hasAnyPermission(['jobs.create', 'jobs.manage'])
  const navigationContext: NavigationAccessInput = {
    accessContext,
    selectedTenantId,
    currentOrganization,
    hasAnyPermission,
  }
  const setupIncomplete = Boolean(
    accessContext
    && !isPlatformUser(accessContext)
    && !accessContext.canAccessMentorAdmin
    && accessContext.onboarding?.required === true
    && accessContext.onboarding?.completed !== true
  )

  // Keep route state in sync with the URL for both custom (`punch:navigate`)
  // and browser (back/forward) navigation. Both paths re-read the location, so
  // workspace selection and the browser buttons resolve identically.
  useEffect(() => {
    const syncLocation = () => setLocationKey(readLocationKey())
    window.addEventListener('popstate', syncLocation)
    window.addEventListener('punch:navigate', syncLocation)
    return () => {
      window.removeEventListener('popstate', syncLocation)
      window.removeEventListener('punch:navigate', syncLocation)
    }
  }, [])

  // Default landing for unsignalled URLs. The precedence (platform → learning-
  // first → admin → supervisor → worker → learning) lives in one place,
  // `resolveDefaultWorkspace`, so the landing surface and the redirect target
  // for an unauthorized workspace can never disagree. An explicit `workspace`
  // in the URL always wins over this value — see `workspaceFromLocation`.
  const defaultFallback: WorkspaceView = resolveDefaultWorkspace(navigationContext)

  // Derive the active workspace from the URL whenever the location (or the
  // resolved default) changes.
  useEffect(() => {
    if (path === '/setup') return
    setActiveWorkspace(workspaceFromLocation(defaultFallback))
  }, [locationKey, path, defaultFallback])

  useEffect(() => {
    if (!setupIncomplete || path === '/setup' || path === '/mentor-admin' || path === '/mentorship-admin') return
    navigateTo('/setup', { replace: true })
  }, [setupIncomplete, path])

  const selectWorkspace = (workspace: WorkspaceView) => {
    navigateTo(workspaceUrl(workspace))
  }

  const resolveGuardedWorkspace = (workspace: WorkspaceView) => guardedWorkspace(workspace, navigationContext)

  const renderWorkspace = () => {
    // A missing context is unresolved access, not permission to enter the fallback workspace.
    if (!accessContext) return (
      <div data-testid="admin-workspace" role={error ? 'alert' : 'status'}>
        <p>{t(error
          ? authenticationRequired ? 'Your session or tenant access changed. Sign in again to complete the required authentication.' : error
          : 'Loading your network access, organizations, and locations...')}</p>
        {error && !loading ? <button type="button" onClick={() => void refresh()}>{t('Retry')}</button> : null}
        {authenticationRequired && !loading ? <button type="button" onClick={async () => {
          await logout()
          const params = new URLSearchParams({ next: '/admin' })
          if (reauthenticationTenantId) params.set('tenantId', reauthenticationTenantId)
          else {
            try { window.localStorage.removeItem('punch.currentTenantId') } catch { /* Optional last-tenant preference. */ }
          }
          window.location.assign(`/login?${params.toString()}`)
        }}>{t('Sign in again')}</button> : null}
      </div>
    )
    if (path === '/setup') return <SetupWizard />
    switch (resolveGuardedWorkspace(activeWorkspace)) {
      case 'platform':
        return <PlatformConsole />
      case 'admin':
        return <AdminWorkspace initialJobDraft={initialJobDraft} onConsumeInitialJobDraft={() => setInitialJobDraft(null)} />
      case 'learning':
        return <LearningWorkspace />
      case 'personal-jobbank':
        return <PersonalJobBankWorkspace />
      case 'job-bank':
        return <JobBankWorkspace />
      case 'market-opportunities':
        return <AgencyMarketOpportunitiesWorkspace />
      case 'mentorship-admin':
        return !accessContext?.tenantId
          ? <MentorAdminWorkspace />
          : (
          <BillingFeatureGate
            featureKey="mentorship.admin"
            title={t('Mentor Admin')}
            description={t('Manage mentors, learners, sponsored programs, sessions, pricing, and reviews.')}
          >
            <MentorAdminWorkspace />
          </BillingFeatureGate>
          )
      case 'worker-marketplace':
        return <WorkerWorkspace />
      case 'worker-mentorship':
        return <WorkerMentorshipPanel />
      case 'shift':
        return <ShiftImport />
      case 'supervisor':
        return <Supervisor />
      case 'mapper':
        return <Mapper canCreateJobs={canManageJobs} onCreateJobFromLocation={(location) => {
          if (!location.id) return
          setInitialJobDraft({
            nonce: Date.now(),
            locationId: location.id,
            ownerOrgId: location.ownerOrgId || location.owner_org_id || location.companyOrgId || location.company_org_id || null,
            source: 'mapper',
          })
          selectWorkspace('admin')
        }} />
      case 'super':
        return <SuperAdmin />
      case 'rbac':
        return <RBAC />
      case 'billing':
        return <Billing />
      case 'platform-pricing':
        return <Billing mode="advanced-pricing" />
      case 'sales':
        return <SalesAdministrationWorkspace />
      case 'sales-crm':
        return <SalesCrmWorkspace />
      default:
        return null
    }
  }
  const visibleWorkspace = resolveGuardedWorkspace(activeWorkspace)
  const workerMobileShellActive = viewport === 'phone'
    && (visibleWorkspace === 'worker-marketplace' || visibleWorkspace === 'worker-mentorship')

  return (
    <WorkspaceCommands workspace={visibleWorkspace} route={locationKey}>
    <div className="page-shell admin-shell" data-testid="admin-shell" data-branded-shell={currentBranding ? 'true' : undefined}>
      <a
        className="skip-link"
        href="#workspace-content"
        onClick={(event) => {
          event.preventDefault()
          document.getElementById('workspace-content')?.focus()
        }}
      >
        {t('Skip to workspace content')}
      </a>
      <div id="globalLoadingBar" className="loading-bar" />
      <Navigation />
      <main id="workspace-content" className="main-content" tabIndex={-1}>
        <div className="container stack-lg" data-testid="main-content">
          {visibleWorkspace === 'admin' ? <AdminExperienceSwitcher /> : null}
          {/*
            On a phone the worker shell is a full-screen application, so a
            workspace card must not sit above its app bar. Switching for a
            multi-workspace worker moves into the worker More screen instead,
            as a secondary control. Every other workspace keeps the existing
            switcher at all widths.
          */}
          {workerMobileShellActive ? null : (
            <WorkspaceSwitcher activeWorkspace={visibleWorkspace} onSelect={selectWorkspace} />
          )}
          <ErrorBoundary
            resetKeys={[visibleWorkspace, path]}
            title={t('Something went wrong')}
            message={t('This workspace could not be displayed. You can retry, and backend permissions still protect every action.')}
            retryLabel={t('Retry')}
            testId="workspace-error-boundary"
          >
            {renderWorkspace()}
          </ErrorBoundary>
        </div>
      </main>
      <AgentLauncher workspace={visibleWorkspace} />
    </div>
    </WorkspaceCommands>
  )
}
